import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MathUtils } from 'three';

import { roundToDecimals } from '@/modules/common/helpers/math';
import { useAppStore } from '@/modules/app/store/useAppStore';

const ANGLE_MIN = 0;
const ANGLE_MAX = 360;
const KNOB_SIZE = 56;
const DRAG_SENSITIVITY = 1;
const VERTICAL_FACTOR = 0.25;
const SHIFT_STEP = 90;

const wrapDegrees360 = (value: number) => ((value % 360) + 360) % 360;
const snapToStep = (value: number, step: number) => Math.round(value / step) * step;

type KnobInputProps = {
  valueDeg: number;
  onChange: (valueDeg: number) => void;
  onCommit: () => void;
  offsetDeg?: number;
  min?: number;
  max?: number;
  wrap?: boolean;
  direction?: 1 | -1;
  invertVisual?: boolean;
  /** Number of decimal places to keep on the emitted value. Defaults to 0 (whole degrees). */
  decimals?: number;
  disabled?: boolean;
};

const KnobInputComponent = ({
  valueDeg,
  onChange,
  onCommit,
  offsetDeg = 0,
  min = ANGLE_MIN,
  max = ANGLE_MAX,
  wrap = true,
  direction = 1,
  invertVisual = false,
  decimals = 0,
  disabled,
}: KnobInputProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const mode = useAppStore((state) => state.mode);

  const pointerIdRef = useRef<number | null>(null);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);
  const valueRef = useRef(valueDeg);

  const isDisabled = useMemo(() => {
    if (disabled !== undefined) return disabled;

    return mode !== 'EDITABLE';
  }, [disabled, mode]);

  useEffect(() => {
    valueRef.current = valueDeg;
  }, [valueDeg]);

  useEffect(() => {
    if (!isDisabled || !isDragging) return;

    pointerIdRef.current = null;

    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsDragging(false);
  }, [isDisabled, isDragging]);

  const normalizeValue = useCallback(
    (value: number) => {
      const rounded = roundToDecimals(value, decimals);

      if (wrap) {
        return wrapDegrees360(rounded);
      }

      return MathUtils.clamp(rounded, min, max);
    },
    [decimals, max, min, wrap],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      event.preventDefault();

      pointerIdRef.current = event.pointerId;
      lastXRef.current = event.clientX;
      lastYRef.current = event.clientY;
      valueRef.current = valueDeg;
      setIsDragging(true);

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [isDisabled, valueDeg],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (isDisabled || !isDragging || pointerIdRef.current !== event.pointerId) return;

      const dx = event.clientX - lastXRef.current;
      const dy = event.clientY - lastYRef.current;

      lastXRef.current = event.clientX;
      lastYRef.current = event.clientY;

      const rawDelta = dx * DRAG_SENSITIVITY + -dy * VERTICAL_FACTOR;
      const delta = rawDelta * direction;

      let nextValue = normalizeValue(valueRef.current + delta);

      if (event.shiftKey) {
        nextValue = normalizeValue(snapToStep(nextValue, SHIFT_STEP));
      }

      valueRef.current = nextValue;
      onChange(nextValue);
    },
    [direction, isDisabled, isDragging, normalizeValue, onChange],
  );

  const finishDrag = useCallback(
    (event?: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;

      if (event && pointerIdRef.current === event.pointerId) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          // no-op
        }
      }

      pointerIdRef.current = null;
      setIsDragging(false);

      if (!isDisabled) {
        onCommit();
      }
    },
    [isDisabled, isDragging, onCommit],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishDrag(event);
    },
    [finishDrag],
  );

  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      finishDrag(event);
    },
    [finishDrag],
  );

  const visualRotation = useMemo(() => {
    const visualValue = invertVisual ? -valueDeg : valueDeg;
    return wrapDegrees360(visualValue + offsetDeg);
  }, [invertVisual, offsetDeg, valueDeg]);

  const cursor = useMemo(() => {
    if (isDisabled) return 'not-allowed';
    if (isDragging) return 'ew-resize';

    return 'grab';
  }, [isDisabled, isDragging]);

  return (
    <Box display='flex' justifyContent='center' py={1}>
      <Box
        role='slider'
        aria-disabled={isDisabled}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={valueDeg}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        sx={(theme) => {
          const background =
            theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${theme.palette.grey[800]}, ${theme.palette.grey[900]})`
              : `linear-gradient(145deg, ${theme.palette.grey[100]}, ${theme.palette.grey[300]})`;

          return {
            position: 'relative',
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            borderRadius: '50%',
            cursor,
            userSelect: 'none',
            touchAction: 'none',
            opacity: isDisabled ? 0.45 : 1,
            background,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDisabled ? 'none' : theme.shadows[2],
            pointerEvents: isDisabled ? 'none' : 'auto',
            '&:hover': {
              boxShadow: isDisabled ? 'none' : theme.shadows[4],
            },
            '&:active': {
              cursor: isDisabled ? 'not-allowed' : 'ew-resize',
            },
          };
        }}
      >
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 6,
            borderRadius: '50%',
            border: `2px solid ${theme.palette.action.disabledBackground}`,
          })}
        />

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 0,
            height: 0,
            transform: `rotate(${wrapDegrees360(offsetDeg)}deg)`,
            transformOrigin: 'center center',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: -2,
              top: -(KNOB_SIZE / 2 - 8),
              width: 4,
              height: 8,
              bgcolor: isDisabled ? theme.palette.action.disabled : '#000',
              borderRadius: 999,
            })}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 0,
            height: 0,
            transform: `rotate(${visualRotation}deg)`,
            transformOrigin: 'center center',
            zIndex: 1,
          }}
        >
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: -1,
              top: -(KNOB_SIZE / 2 - 12),
              width: 2,
              height: KNOB_SIZE / 2 - 14,
              bgcolor: isDisabled ? theme.palette.action.disabled : theme.palette.primary.main,
              borderRadius: 999,
              boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
            })}
          />
        </Box>

        <Box
          sx={(theme) => ({
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 12,
            height: 12,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDisabled ? 'none' : theme.shadows[1],
            zIndex: 2,
          })}
        />
      </Box>
    </Box>
  );
};

export const KnobInput = memo(KnobInputComponent);
